<template>
  <div>
    <invoice-attachments-modal
      v-model="attachmentModal"
      title="Contract Attachments"
      :attachments="fileAttachments"
      :loading="attachmentsLoading"
      :uploading="attachmentUploading"
      :reset-key="attachmentUploadResetKey"
      :deleting-id="deletingAttachmentId"
      @view="viewAttachment"
      @download="downloadAttachment"
      @delete="confirmDeleteAttachment"
      @upload="uploadAttachment"
      @invalid-files="showOversizedFiles"
    />

    <contract-list-add-new
      :is-add-new-contract-sidebar-active.sync="isAddNewContractSidebarActive"
      @refetch-data="refetchData"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search & Add -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search code, agreement ref, customer, description..."
              />
              <b-button
                variant="primary"
                @click="isAddNewContractSidebarActive = true"
              >
                <span class="text-nowrap">Add New Contract</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refContractsListTable"
        class="position-relative"
        :items="fetchContracts"
        responsive
        :fields="tableColumns"
        primary-key="Code"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy.sync="isTableBusy"
      >
        <template #table-busy>
          <div class="text-center text-muted my-2">
            <b-spinner
class="align-middle mr-1"
small
/>
            <strong>Loading...</strong>
          </div>
        </template>

        <!-- Column: Issued Date -->
        <template #cell(IssuedDate)="data">
          <span class="text-nowrap">{{ dateFormat(data.item.IssuedDate) }}</span>
        </template>

        <!-- Column: Start Date -->
        <template #cell(StartDate)="data">
          <span class="text-nowrap">{{ dateFormat(data.item.StartDate) }}</span>
        </template>

        <!-- Column: End Date -->
        <template #cell(EndDate)="data">
          <span class="text-nowrap">{{ dateFormat(data.item.EndDate) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            variant="success"
            size="sm"
            @click="showAttachments(data.item.Code)"
          >
            View Attachments
          </b-button>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalContracts"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
icon="ChevronLeftIcon"
size="18"
/>
              </template>
              <template #next-text>
                <feather-icon
icon="ChevronRightIcon"
size="18"
/>
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import { uploadSuccess, uploadFailed } from '@/libs/upload-alert'
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BPagination, BSpinner,
  BModal, BForm, BFormFile,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import moment from 'moment'
import { getToken } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useContractsList from './useContractsList'
import ContractListAddNew from './Add.vue'
import InvoiceAttachmentsModal from '../invoices/components/InvoiceAttachmentsModal.vue'

export default {
  components: {
    ContractListAddNew,
    InvoiceAttachmentsModal,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BModal,
    BForm,
    BFormFile,
    vSelect,
  },
  data() {
    return {
      isAddNewContractSidebarActive: false,
      isTableBusy: false,
      attachmentModal: false,
      currentCode: '',
      fileAttachments: [],
      attachmentsLoading: false,
      attachmentUploading: false,
      attachmentUploadResetKey: 0,
      deletingAttachmentId: '',
    }
  },
  setup() {
    const {
      fetchContracts,
      tableColumns,
      perPage,
      perPageOptions,
      currentPage,
      totalContracts,
      dataMeta,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refContractsListTable,
      refetchData,
    } = useContractsList()

    return {
      fetchContracts,
      tableColumns,
      perPage,
      perPageOptions,
      currentPage,
      totalContracts,
      dataMeta,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refContractsListTable,
      refetchData,
    }
  },
  methods: {
    dateFormat(value) {
      if (!value) return '-'
      return moment(String(value)).format('MM/DD/YYYY')
    },
    downloadAttachment(item) {
      this.fetchAttachmentBlob(item).then(blob => {
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = item.filename || 'attachment'
        document.body.appendChild(link)
        link.click()
        link.remove()
        // Mobile browsers may need longer to start reading a large blob.
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
      }).catch(() => this.notifyAttachmentError('Unable to download attachment'))
    },
    viewAttachment(item) {
      const previewWindow = window.open('', '_blank')
      this.fetchAttachmentBlob(item).then(blob => {
        const objectUrl = URL.createObjectURL(blob)
        if (previewWindow) previewWindow.location.href = objectUrl
        else window.open(objectUrl, '_blank')
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
      }).catch(() => {
        if (previewWindow) previewWindow.close()
        this.notifyAttachmentError('Unable to preview attachment')
      })
    },
    fetchAttachmentBlob(item) {
      return axios.get(item.url, {
        responseType: 'blob',
        headers: { 'X-Token-Access': `Bearer ${getToken()}` },
      }).then(response => new Blob([response.data], {
        type: this.attachmentMimeType(item.filename),
      }))
    },
    attachmentMimeType(filename) {
      const extension = String(filename || '').split('.').pop().toLowerCase()
      const types = {
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        svg: 'image/svg+xml',
        txt: 'text/plain',
      }
      return types[extension] || 'application/octet-stream'
    },
    notifyAttachmentError(title) {
      this.$toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'danger' } })
    },
    async uploadAttachment(files) {
      const userToken = getToken()
      if (!files || !files.length || this.attachmentUploading) return

      const formData = new FormData()
      for (let i = 0; i < files.length; i += 1) {
        formData.append('attachments[]', files[i])
      }
      formData.append('code', this.currentCode)
      this.attachmentUploading = true

      try {
        const response = await axios.post('https://api.geolims.com/invoice_service/addMoreContractAttachment', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Token-Access': `Bearer ${userToken}`,
          },
        })

        if (response.status === 200 && response.data.Status === 1) {
          this.showAttachments(this.currentCode)
          this.attachmentUploadResetKey += 1
          uploadSuccess(this, 'Attachments uploaded successfully')
        } else {
          uploadFailed(this, { response })
        }
      } catch (error) {
        console.error('Error uploading files:', error)
        uploadFailed(this, { error })
      } finally {
        this.attachmentUploading = false
      }
    },
    showOversizedFiles(files) {
      this.$toast({ component: ToastificationContent, props: { title: `${files.length} file(s) exceed the 100 MB limit`, icon: 'AlertTriangleIcon', variant: 'warning' } })
    },
    confirmDeleteAttachment(item) {
      this.$bvModal.msgBoxConfirm(`Delete ${item.filename}?`, {
        title: 'Delete attachment',
        okTitle: 'Delete',
        okVariant: 'danger',
        cancelTitle: 'Cancel',
        centered: true,
      }).then(confirmed => {
        if (confirmed) this.deleteAttachment(item.id)
      })
    },
    deleteAttachment(id) {
      const userToken = getToken()
      const headers = {
        'X-Token-Access': `Bearer ${userToken}`,
        'Content-Type': 'application/json',
      }
      const body = { id }
      if (this.deletingAttachmentId) return
      this.deletingAttachmentId = id
      axios
        .post('https://api.geolims.com/invoice_service/deleteContractAttachment', body, { headers })
        .then(response => {
          if (response.status === 200 && response.data.Status === 1) {
            this.showAttachments(this.currentCode)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Delete Attachment Succeed',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error delete attachment',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
        .catch(e => {
          console.log(e)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error delete attachment',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.deletingAttachmentId = ''
        })
    },
    showAttachments(ContractCode) {
      this.currentCode = ContractCode
      this.attachmentsLoading = true
      const userToken = getToken()
      const headers = {
        'Content-Type': 'multipart/form-dataitem',
        'X-Token-Access': `Bearer ${userToken}`,
      }
      axios
        .get(`https://api.geolims.com/invoice_service/getContractAttachments/${ContractCode}`, { headers })
        .then(response => {
          if (response.data.Status === 1) {
            this.fileAttachments = response.data.Attachments || []
            this.attachmentModal = true
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Something went wrong',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
        .finally(() => {
          this.attachmentsLoading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
